import React, { useEffect, useRef } from "react";
import "./modal.scss";
import CloseIcon from "../../SVG/close";

export const ModalPortalContext =
  React.createContext<React.RefObject<HTMLElement | null> | null>(null);

/** Ref to the portal mount inside an open Modal. Pass to `portalTarget` on portaled children. */
export function useModalPortalTarget():
  | React.RefObject<HTMLElement | null>
  | undefined {
  return React.useContext(ModalPortalContext) ?? undefined;
}

interface ModalProps {
  dialogId: string;
  onSave: () => void | Promise<void>;
  children: React.ReactNode;
  title: string;
  /** Optional content rendered in the header after the title. */
  headerContent?: React.ReactNode;
  saveButtonText: string;
  onCancel?: () => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  cancelText: string;
  heightPercentage?: number;
  /**
   * Auto-fit modal to its content, capping the height at this percentage of the viewport.
   * When set, the modal stays compact for short content and scrolls inside the body
   * once content exceeds the cap. Takes precedence over `heightPercentage`.
   */
  maxHeightPercentage?: number;
  showSingleButton?: boolean;
  disableSave?: boolean;
  /** Disable the cancel button. @default false */
  disableCancel?: boolean;
  hideFooter?: boolean;
  autoCloseOnSave?: boolean; // New prop to control auto-close behavior
  disableOnCloseFunc?: boolean;
  hideCloseButton?: boolean
  disableCloseOnOutsideClick?: boolean
  /** Stretch the dialog to nearly the full viewport width. */
  fullWidth?: boolean
}

export function Modal(props: ModalProps) {
  const portalRootRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    if (!props.disableOnCloseFunc) {
      closeModal(props.dialogId, props.onClose);
    }
    if (props.onCancel) {
      props.onCancel();
    }
  };

  

  useEffect(() => {
    const dialogEle = document.getElementById(props.dialogId);
    const handleClickOutside = (event: any) => {
      if (event.target === dialogEle) {
        closeModal(props.dialogId, props.onClose);
      }
    };
    
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (props.disableCloseOnOutsideClick) {
          event.preventDefault();
        } else {
          closeModal(props.dialogId, props.onClose);
        }
      }
    };

    if (!props.disableCloseOnOutsideClick) {
      dialogEle?.addEventListener("click", handleClickOutside);
    }
    document.addEventListener("keydown", handleEscapeKey);
    
    return () => {
      dialogEle?.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [props.dialogId, props.disableCloseOnOutsideClick]);

  

  const isAutoFit = props.maxHeightPercentage != null;
  const cappedPct = props.maxHeightPercentage ?? props.heightPercentage;

  return (
    <>
      <dialog
        id={props.dialogId}
        className={[
          "custom-modal",
          isAutoFit && "custom-modal--auto-fit",
          props.fullWidth && "custom-modal--full-width",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          height: isAutoFit ? "fit-content" : "auto",
          maxHeight: cappedPct ? `${cappedPct}%` : undefined,
        }}
      >
        <ModalPortalContext.Provider value={portalRootRef}>
          <div
            ref={portalRootRef}
            className="custom-modal__portal-root"
            aria-hidden="true"
          />
          <div className="custom-modal-container">
            <div className="dialog-header">
              <div className="dialog-header-main">
                <div className="dialog-header-title">{props.title}</div>
                {props.headerContent != null && (
                  <div className="dialog-header-content">{props.headerContent}</div>
                )}
              </div>
              {!props.hideCloseButton && (
                <div
                  className="dialog-close-button"
                  onClick={() => closeModal(props.dialogId, props.onClose)}
                >
                  <CloseIcon />
                </div>
              )}
            </div>
            <div className="dialog-body">{props.children}</div>
            {!props.hideFooter && (
              <div className="dialog-footer">
                {props.showSingleButton ? (
                  <button
                    className="dialog-footer-button save"
                    onClick={props.onSave}
                    disabled={props.disableSave}
                  >
                    {props.saveButtonText}
                  </button>
                ) : (
                  <>
                    <button
                      className="dialog-footer-button cancel"
                      onClick={handleCancel}
                      disabled={props.disableCancel}
                    >
                      {props.cancelText}
                    </button>
                    <button
                      className="dialog-footer-button save"
                      onClick={props.onSave}
                      disabled={props.disableSave}
                    >
                      {props.saveButtonText}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </ModalPortalContext.Provider>
      </dialog>
    </>
  );
}

export function openModal(arg0: string): void {
  const modalEle = document.getElementById(arg0) as HTMLDialogElement;
  modalEle.showModal();
  modalEle.classList.add("show");
}

export function closeModal(arg0: string, onCloseFunc?: any,): void {
  const modalEle = document.getElementById(arg0) as HTMLDialogElement;
  if (onCloseFunc) {
    onCloseFunc();
  }
  modalEle.close();
  modalEle.classList.remove("show");
}
